import { isCorrelationId, newCorrelationId } from '../../errors/correlation.js';
import { hashEntity } from '../../telemetry/pii-scrub.js';
import type { SubmissionResult } from '../types.js';

export const TX_LIFECYCLE_EVENT = 'tx.lifecycle';
export const TX_LIFECYCLE_SCHEMA_VERSION = 1;

const SUCCESS_SAMPLE_RATE = 0.1;
const HASH_BUCKET_COUNT = 0x1_0000_0000;

export interface SubmissionDiagnostics {
  clientCorrelationId?: string;
  backendRequestId?: string;
  backendCorrelationId?: string;
  providerActivityIdHash?: string;
  buildDurationMs?: number;
  submitDurationMs?: number;
  confirmationDurationMs?: number;
  totalStatusRequests?: number;
  successfulStatusResponses?: number;
  rateLimitResponses?: number;
  transportFailures?: number;
  wsSubscriptionFallback?: boolean;
  pollingTransportFailures?: number;
  expiryStatusLookups?: number;
  blockHeightTransportFailures?: number;
  simulationOutcome?: 'program_error' | 'rpc_error';
}

/** Internal result enrichment stripped by submitTransaction before returning. */
export type InternalSubmissionResult = SubmissionResult & {
  diagnostics?: SubmissionDiagnostics;
};

/** ULID accepted by the backend correlation middleware. */
export function createTransactionTraceId(): string {
  return newCorrelationId();
}

/** Keep telemetry correlation keys within the backend's accepted ULID range. */
export function isValidTransactionTraceId(candidate: string | undefined): candidate is string {
  return isCorrelationId(candidate) && candidate[0] !== undefined && candidate[0] <= '7';
}

/** Keep caller correlation only when the backend middleware can accept it. */
export function resolveTransactionTraceId(candidate: string | undefined): string {
  return isValidTransactionTraceId(candidate) ? candidate : createTransactionTraceId();
}

/** Stable sampling keeps every event for a selected trace in the same bucket. */
export function shouldSampleSuccessfulTrace(traceId: string): boolean {
  const bucket = Number.parseInt(hashEntity(traceId), 16) / HASH_BUCKET_COUNT;
  return bucket < SUCCESS_SAMPLE_RATE;
}

/** Privacy-safe join key for Solana and provider identifiers. */
export function hashDiagnosticEntity(value: string): string {
  return `e_${hashEntity(value)}`;
}

export function boundedDiagnosticText(value: unknown, maxLength = 200): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return value.slice(0, maxLength);
}
